import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { EditBankAccountInput } from '@/lib/payments/paymentAccount.types';
import {
  type MyMemberInfo,
  mapToAutoPaySummary,
  memberInfoKey,
  requestEnableMyAutoPay,
} from '../memberInfo/memberInfoApi';
import {
  promoteDefault,
  type SavedPaymentMethodsData,
  savedPaymentMethodsKey,
  toBillingAddressInput,
} from '../paymentApi';
import { BANK_ACCOUNT_TYPE_TO_GQL, PaymentEditBankAccountError, requestEditBankAccount } from './bankAccountApi';

interface UseEditBankAccountOptions {
  onSuccess?: () => void;
}

/**
 * Edits a saved bank account in place. No verify step — routing/account digits are immutable and
 * never re-enter the request, so unlike useAddBankAccount there's no gateway payload to sanitize
 * out of error tracking.
 */
export function useEditBankAccount({ onSuccess }: UseEditBankAccountOptions = {}) {
  const { toast } = useToast();
  const tError = useErrorTranslation();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: EditBankAccountInput) => {
      const updatedAccount = await requestEditBankAccount(input.id, {
        accountType: BANK_ACCOUNT_TYPE_TO_GQL[input.accountType],
        hsa: input.isHsa,
        isDefault: input.makeDefault,
        nameOnAccount: input.billing.name,
        billingAddress: toBillingAddressInput(input.billing),
      });

      // Skip the call entirely when we already know (from the same cache that locks the checkbox)
      // that the member is enrolled — avoids a redundant round trip and Salesforce read. The
      // backend's own idempotent pre-check remains the fallback if this cache is stale.
      const cachedMemberInfo = queryClient.getQueryData<MyMemberInfo>(memberInfoKey);
      const alreadyEnrolled = cachedMemberInfo ? mapToAutoPaySummary(cachedMemberInfo).autoPayEnrolled : false;
      if (input.autoPay && !alreadyEnrolled) {
        // Enrollment failure must not block the account save — swallow and track only.
        try {
          await requestEnableMyAutoPay();
        } catch (err) {
          if (err instanceof Error) {
            trackException(err, { source: 'enableMyAutoPay', context: 'editBankAccount' });
          }
        }
      }

      return updatedAccount;
    },
    onSuccess: (updatedAccount) => {
      queryClient.setQueryData<SavedPaymentMethodsData>(savedPaymentMethodsKey, (existing) => {
        const withUpdatedAccount: SavedPaymentMethodsData = {
          cards: existing?.cards ?? [],
          bankAccounts: (existing?.bankAccounts ?? []).map((account) =>
            account.id === updatedAccount.id ? updatedAccount : account,
          ),
        };
        // The edited account's new default (if any) atomically demotes the prior one server-side,
        // across both cards and bank accounts — mirror that here so the optimistic list doesn't
        // show a stale badge anywhere until the invalidate-triggered refetch reconciles it.
        return updatedAccount.isDefault ? promoteDefault(withUpdatedAccount, updatedAccount.id) : withUpdatedAccount;
      });
      void queryClient.invalidateQueries({ queryKey: savedPaymentMethodsKey });
      void queryClient.invalidateQueries({ queryKey: memberInfoKey });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      // PaymentEditBankAccountError wraps editBankAccount's Failure variant (NOT_FOUND, deliberately
      // overloaded — see bankAccountApi.ts). No gateway data in the request variables here, so
      // unlike useAddBankAccount there's nothing to sanitize before tracking.
      const code = error instanceof PaymentEditBankAccountError ? error.code : extractGqlErrorCode(error);
      if (error instanceof Error) {
        trackException(error, { source: 'editBankAccount', errorCode: code });
      }
      toast({ title: tError('title'), description: tError(code), variant: 'destructive' });
    },
  });

  return { submit: (input: EditBankAccountInput) => mutation.mutate(input), isPending: mutation.isPending };
}
